import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type FeatureFlagsE } from '@bbk/contracts/feature-flags';
import { selectAppId, selectCompanyInternalName } from './active-context';
import { bbkApiSlice } from './api';

type GetUserFFsArgs = {
  companyKey: string;
  appEncodedId: string;
};
export const featureFlagsSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getGlobalFFs: build.query<FeatureFlagsE[], void>({
      query: () => ({
        url: `/app/enabledFEFeatureFlags`,
        params: { nonce: Date.now() },
      }),
    }),
    getUserFFs: build.query<FeatureFlagsE[], GetUserFFsArgs>({
      query: ({ companyKey, appEncodedId }) => ({
        url: `/api/enabledFEFeatureFlags`,
        params: {
          companyKey,
          application_id: appEncodedId,
        },
      }),
    }),
  }),
});

const FF_POLL_DELAY = 5 * 60 * 1000; // 5 mins

export const useQueryFeatureFlags = () => {
  const companyKey = useSelector(selectCompanyInternalName);
  const appEncodedId = useSelector(selectAppId);

  const globalRes = featureFlagsSlice.endpoints.getGlobalFFs.useQuery(
    undefined,
    {
      pollingInterval: FF_POLL_DELAY,
    }
  );

  const userRes = featureFlagsSlice.endpoints.getUserFFs.useQuery(
    {
      companyKey,
      appEncodedId,
    },
    {
      skip:
        !companyKey ||
        !appEncodedId ||
        companyKey === '_' ||
        appEncodedId === '_',
      pollingInterval: FF_POLL_DELAY,
    }
  );

  const data = useMemo(() => {
    const globalFFs = globalRes.data ?? [];
    const userFFs = userRes.data ?? [];
    return Array.from(new Set([...globalFFs, ...userFFs]));
  }, [JSON.stringify(globalRes.data), JSON.stringify(userRes.data)]);

  return useMemo(() => {
    const {
      isLoading: globalIsLoading,
      isUninitialized: globalIsUninitialized,
      isSuccess: globalIsSuccess,
    } = globalRes;
    const {
      isLoading: userIsLoading,
      isUninitialized: userIsUnitialized,
      isSuccess: userIsSuccess,
    } = userRes;
    return {
      isUninitialized: globalIsUninitialized || userIsUnitialized,
      isLoading: globalIsLoading || userIsLoading,
      isSuccess: globalIsSuccess && userIsSuccess,
      hasCurrentData:
        globalRes.currentData !== undefined &&
        userRes.currentData !== undefined,
      isError: globalRes.isError || userRes.isError,
      refetch: () => {
        if (!globalRes.isUninitialized) void globalRes.refetch();
        if (!userRes.isUninitialized) void userRes.refetch();
      },
      data,
    };
  }, [globalRes, userRes]);
};

export { FeatureFlagsE } from '@bbk/contracts/feature-flags';

export const useFeatureFlags = (featureFlag: FeatureFlagsE): boolean => {
  const { data: featureFlags } = useQueryFeatureFlags();

  return useMemo(
    () => featureFlags.includes(featureFlag),
    [featureFlags, featureFlag]
  );
};

export const useFeatureFlagsWithLoading = (
  featureFlag: FeatureFlagsE
): {
  enabled: boolean;
  isLoading: boolean;
} => {
  const { data: featureFlags, isLoading, isSuccess } = useQueryFeatureFlags();

  const enabled = useMemo(
    () => featureFlags.includes(featureFlag),
    [featureFlags, featureFlag]
  );

  return { enabled, isLoading: isLoading || !isSuccess };
};
